import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  ControlRow,
  SelectOption,
  emptyFilledFilters,
  LookupValue
} from 'store/ducks/InternalControls/types';
import CustomSelect from 'components/CustomSelect';
import CustomFileUpload from 'components/CustomFileUpload';
import dayjs from 'dayjs';
import CustomDatePicker from 'components/CustomDatePicker';
import Helpers from 'utils/Helpers';
import {
  base_fields_control_test_operational,
  instance_fields_control_test_operational
} from './riskControlFieldsToDisable';
import * as Styled from '../pages/InternalControlsMain/RiskControl.styled';

export const getOperationalEffectivenessColumns = (
  testObjectiveness: SelectOption[],
  controls: ControlRow[],
  performerToE: SelectOption[],
  operatingEffectiveness: SelectOption[],
  designEffectiveness: SelectOption[],
  testingMethod: SelectOption[],
  testType: SelectOption[],
  testResult: SelectOption[],
  evidenceType: SelectOption[],
  setControls: (controls: ControlRow[]) => void,
  trackingSetControls: (controls: ControlRow[]) => void,
  newControlIds?: Set<number>,
  isScopedView?: boolean,
  t?: (key: string) => string
): ColumnsType<ControlRow> => {
  const shouldDisable = (record: ControlRow, fieldKey: string) => {
    if (record.notUsed) return true;

    if (isScopedView) {
      return base_fields_control_test_operational.has(fieldKey);
    }

    return instance_fields_control_test_operational.has(fieldKey);
  };

  return [
    {
      title: t ? t('CONTROL_NAME') : 'Control Name',
      dataIndex: 'controlName',
      key: 'controlName',
      width: 200,
      fixed: 'left',
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlName'),
      render: (value: string, record, index) => {
        if (record.notUsed) {
          return (
            <Tooltip title='Disabled due to a previous Risk/Control type change.'>
              <Styled.DisabledTooltipDiv>
                {value || '—'}
              </Styled.DisabledTooltipDiv>
            </Tooltip>
          );
        }
        return (
          <>
            <Styled.GlobalTooltipStyles />
            <Tooltip
              title={
                value
                  ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                  : ''
              }
              placement='topLeft'
              mouseEnterDelay={0.1}
              mouseLeaveDelay={0.1}
              arrow={false}
            >
              <Styled.ResizableTextArea
                value={value || ''}
                placeholder='Enter control name'
                rows={2}
                disabled={shouldDisable(record, 'controlName')}
                onChange={(e) => {
                  const newControls = [...controls];
                  const actualIndex = newControls.findIndex(
                    (c) => c.id === record.id
                  );
                  if (actualIndex !== -1) {
                    newControls[actualIndex].controlName = e.target.value;
                    trackingSetControls(newControls);
                  }
                }}
              />
            </Tooltip>
          </>
        );
      }
    },
    {
      title: t ? t('DESCRIPTION') : 'Description',
      dataIndex: 'controlDescription',
      key: 'controlDescription',
      width: 250,
      fixed: 'left',
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlDescription'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value || ''}
              placeholder='Enter control description'
              rows={3}
              disabled={shouldDisable(record, 'controlDescription')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex].controlDescription = e.target.value;
                  trackingSetControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t ? t('TEST_OBJECTIVE_2ND_LINE') : 'Test Objective (2nd line)',
      dataIndex: 'testObjectiveness',
      key: 'testObjectiveness',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('testObjectiveness'),
      render: (value: string | LookupValue, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              typeof value === 'string'
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : value?.name
                ? value.name.substring(0, 50) +
                  (value.name.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={typeof value === 'string' ? value : value?.name || ''}
              placeholder='Enter test objective'
              disabled={shouldDisable(record, 'testObjectiveness')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex].testObjectiveness = e.target.value;
                  trackingSetControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t ? t('TESTING_METHOD_2ND_LINE') : 'Testing method (2nd line)',
      dataIndex: 'testingMethod',
      key: 'testingMethod',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('testingMethod'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={testingMethod}
          disabled={shouldDisable(record, 'testingMethod')}
          placeholder='Select Testing Method'
          onChange={(id: number) => {
            const selected = testingMethod.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].testingMethod = {
                id,
                name: selected?.label ?? null
              };
              trackingSetControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('TEST_DESCRIPTION') : 'Test Description',
      dataIndex: 'testDescription',
      key: 'testDescription',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('testDescription'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value}
              placeholder='Enter test description'
              rows={3}
              disabled={shouldDisable(record, 'testDescription')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex].testDescription = e.target.value;
                  trackingSetControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t ? t('TEST_TYPE_2ND_LINE') : 'Test type (2nd line)',
      dataIndex: 'testType',
      key: 'testType',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('testType'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={testType}
          placeholder='Select Test Type'
          disabled={shouldDisable(record, 'testType')}
          onChange={(id: number) => {
            const selected = testType.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].testType = {
                id,
                name: selected?.label ?? null
              };
              trackingSetControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('TEST_RESULT_2ND_LINE') : 'Test Result (2nd line)',
      dataIndex: 'testResult',
      key: 'testResult',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('testResult'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={testResult}
          placeholder='Select Test Result'
          disabled={shouldDisable(record, 'testResult')}
          onChange={(id: number) => {
            const selected = testResult.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].testResult = {
                id,
                name: selected?.label ?? null
              };
              trackingSetControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('EVIDENCE_DESCRIPTION') : 'Evidence Description',
      dataIndex: 'evidenceDescription',
      key: 'evidenceDescription',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('evidenceDescription'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value}
              placeholder='Enter evidence description'
              rows={3}
              disabled={shouldDisable(record, 'evidenceDescription')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex].evidenceDescription = e.target.value;
                  trackingSetControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t ? t('EVIDENCE_TYPE_2ND_LINE') : 'Evidence type (2nd line)',
      dataIndex: 'evidenceType',
      key: 'evidenceType',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('evidenceType'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={evidenceType}
          placeholder='Select Evidence Type'
          disabled={shouldDisable(record, 'evidenceType')}
          onChange={(id: number) => {
            const selected = evidenceType.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].evidenceType = {
                id,
                name: selected?.label ?? null
              };
              trackingSetControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('EVIDENCE_FILE_2') : 'Evidence File 2',
      dataIndex: 'evidenceFile2',
      key: 'evidenceFile2',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('evidenceFile2'),
      render: (value: string, record, index) => {
        const isDisabled = shouldDisable(record, 'evidenceFile2');
        return (
          <CustomFileUpload
            defaultFileList={value ? [value] : []}
            onChange={(fileList) => {
              if (isDisabled) return;
              const newControls = [...controls];
              const actualIndex = newControls.findIndex(
                (c) => c.id === record.id
              );
              if (actualIndex !== -1) {
                newControls[actualIndex].evidenceFile2 =
                  fileList.length > 0
                    ? fileList[0].url || fileList[0].name
                    : null;
                trackingSetControls(newControls);
              }
            }}
          />
        );
      }
    },
    {
      title: t
        ? t('OPERATING_EFFECTIVENESS_2ND_LINE')
        : 'Operating Effectiveness (2nd line)',
      dataIndex: 'operatingEffectiveness',
      key: 'operatingEffectiveness',
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>(
        'operatingEffectiveness'
      ),
      width: 200,
      ellipsis: true,
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={operatingEffectiveness}
          disabled={shouldDisable(record, 'operatingEffectiveness')}
          placeholder='Select Operating Effectiveness'
          onChange={(id: number) => {
            const selected = operatingEffectiveness.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].operatingEffectiveness = {
                id,
                name: selected?.label ?? null
              };
              trackingSetControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t
        ? t('DESIGN_EFFECTIVENESS_2ND_LINE')
        : 'Design Effectiveness (2nd line)',
      dataIndex: 'designEffectiveness',
      key: 'designEffectiveness',
      width: 200,
      ellipsis: true,
      render: (value) => (
        <CustomSelect value={value?.name ?? undefined} disabled />
      )
    },
    {
      title: t ? t('PERFORMER_TOE_2ND_LINE') : 'Performer ToE (2nd line)',
      dataIndex: 'performerToE',
      key: 'performerToE',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('performerToE'),
      render: (value: string | LookupValue, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              typeof value === 'string'
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : value?.name
                ? value.name.substring(0, 50) +
                  (value.name.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={typeof value === 'string' ? value : value?.name || ''}
              placeholder='Enter performer ToE'
              disabled={shouldDisable(record, 'performerToE')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex].performerToE = e.target.value;
                  trackingSetControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t
        ? t('LAST_TEST_DATE_OPERATIONAL_2ND_LINE')
        : 'Last Test Date Operational (2nd line)',
      dataIndex: 'lastTestDateOperational',
      key: 'lastTestDateOperational',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>(
        'lastTestDateOperational'
      ),
      render: (value: string | null, record, index) => (
        <Styled.DatePickerOperational>
          <CustomDatePicker
            value={value ? dayjs(value) : null}
            disabled={shouldDisable(record, 'lastTestDateOperational')}
            onChange={(date: dayjs.Dayjs | null) => {
              const newControls = [...controls];
              const actualIndex = newControls.findIndex(
                (c) => c.id === record.id
              );
              if (actualIndex !== -1) {
                newControls[actualIndex].lastTestDateOperational = date
                  ? date.format('YYYY-MM-DD')
                  : null;
                trackingSetControls(newControls);
              }
            }}
          />
        </Styled.DatePickerOperational>
      )
    },
    {
      title: t ? t('NEXT_TEST_DATE_2ND_LINE') : 'Next Test Date (2nd line)',
      dataIndex: 'nextTestDate',
      key: 'nextTestDate',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('nextTestDate'),
      render: (value: string | null, record, index) => (
        <Styled.DatePickerNext>
          <CustomDatePicker
            value={value ? dayjs(value) : null}
            disabled={shouldDisable(record, 'nextTestDate')}
            onChange={(date: dayjs.Dayjs | null) => {
              const newControls = [...controls];
              const actualIndex = newControls.findIndex(
                (c) => c.id === record.id
              );
              if (actualIndex !== -1) {
                newControls[actualIndex].nextTestDate = date
                  ? date.format('YYYY-MM-DD')
                  : null;
                trackingSetControls(newControls);
              }
            }}
          />
        </Styled.DatePickerNext>
      )
    },
    {
      title: t ? t('KEY_CONTROL') : 'Key Control',
      dataIndex: 'keyControl',
      key: 'keyControl',
      width: 120,
      ellipsis: true,
      render: (value: boolean) => (
        <Styled.KeyControlInput
          value={value ? 'true' : 'false'}
          disabled
          size='small'
        />
      )
    }
  ];
};
